import { defineComponent, ref, reactive, computed, inject, watch, onMounted, onBeforeUnmount, openBlock, createElementBlock, mergeProps, unref, renderSlot, onActivated, onDeactivated, createBlock, withCtx, createCommentVNode, createElementVNode, h, createApp } from 'vue';
import { offset, flip, shift, arrow, computePosition, getOverflowAncestors } from '@floating-ui/dom';
import { LocalEventHandler, normalizeDelay, globalEventHandler, nextAnimationFrame, renameAttribute, addCloserListeners, removeCloserListeners } from '@omnica/popper';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var script$2 = /*#__PURE__*/ defineComponent({
    __name: 'UiPopperFloating',
    props: {
        target: {
            type: Function,
            required: true,
        },
        container: {
            type: null,
            validator: (value) => value === null || value instanceof Element || typeof value === 'string',
            default: null,
        },
        boundary: {
            type: [String, Element, Array],
            default: undefined,
        },
        offsetMainAxis: {
            type: [Number, String],
            validator: (value) => !isNaN(Number(value)),
            default: 8,
        },
        offsetCrossAxis: {
            type: [Number, String],
            validator: (value) => !isNaN(Number(value)),
            default: 0,
        },
        placement: {
            type: String,
            default: 'bottom',
        },
        strategy: {
            type: String,
            default: 'absolute',
        },
        overflow: {
            type: Array,
            default: () => ['flip', 'shift'],
        },
    },
    setup(__props, { expose }) {
        const props = __props;
        const root = ref(null);
        const events = new LocalEventHandler();
        const state = reactive({
            floating: null,
        });
        const style = computed(() => {
            if (state.floating) {
                const { strategy, x, y } = state.floating;
                return {
                    position: strategy,
                    transform: `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`,
                };
            }
            return {};
        });
        let observer = null;
        const globals = inject('$omnicaPopper', {
            container: null,
        });
        const adjustArrow = (arrow) => {
            if (!state.floating || !state.floating.arrow) {
                return;
            }
            const { x, y } = state.floating.arrow;
            const [side] = state.floating.placement.split('-');
            Object.assign(arrow.style, {
                position: state.floating.strategy,
                left: x != null ? `${x}px` : '',
                top: y != null ? `${y}px` : '',
                right: '',
                bottom: '',
                [{
                    left: 'right',
                    top: 'bottom',
                    right: 'left',
                    bottom: 'top',
                }[side]]: '-4px',
            });
        };
        const adjust = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const target = props.target();
            if (!target || !root.value) {
                return;
            }
            const middleware = [];
            if (props.offsetMainAxis || props.offsetCrossAxis) {
                middleware.push(offset({
                    mainAxis: Number(props.offsetMainAxis),
                    crossAxis: Number(props.offsetCrossAxis),
                }));
            }
            if ((_a = props.overflow) === null || _a === void 0 ? void 0 : _a.includes('flip')) {
                middleware.push(flip({
                    boundary: props.boundary,
                }));
            }
            if ((_b = props.overflow) === null || _b === void 0 ? void 0 : _b.includes('shift')) {
                middleware.push(shift({
                    boundary: props.boundary,
                    padding: 12,
                }));
            }
            const arrowEl = (_d = (_c = root.value) === null || _c === void 0 ? void 0 : _c.querySelector('[data-arrow]')) !== null && _d !== void 0 ? _d : null;
            if (arrowEl) {
                middleware.push(arrow({
                    element: arrowEl,
                    padding: arrowEl.offsetWidth / 3,
                }));
            }
            const { placement, strategy, x, y, middlewareData } = yield computePosition(target, root.value, {
                middleware,
                placement: props.placement,
                strategy: props.strategy,
            });
            state.floating = { placement, strategy, x, y, arrow: middlewareData.arrow };
            if (arrowEl) {
                adjustArrow(arrowEl);
            }
        });
        const contains = (el) => { var _a, _b; return (_b = (_a = root.value) === null || _a === void 0 ? void 0 : _a.contains(el)) !== null && _b !== void 0 ? _b : false; };
        // Don't remove popper instance, just the HTML element
        const detach = () => {
            const el = root.value;
            if (el && el.parentNode) {
                el.parentNode.removeChild(el);
            }
        };
        const teleport = () => {
            var _a;
            if (!root.value) {
                return;
            }
            const arg = (_a = props.container) !== null && _a !== void 0 ? _a : globals.container;
            const container = typeof arg === 'string'
                ? document.querySelector(arg)
                : arg === null
                    ? document.body
                    : arg;
            if (!container) {
                throw new Error('No container for popper: ' + arg);
            }
            container.appendChild(root.value);
        };
        expose({
            adjust,
            contains,
            detach,
            teleport,
        });
        const watchScroll = () => {
            const target = props.target();
            if (target && root.value) {
                events.on([
                    ...getOverflowAncestors(target),
                    ...getOverflowAncestors(root.value),
                ], 'scroll', adjust);
            }
            else {
                events.clear();
            }
        };
        watch([
            () => props.target,
            () => props.boundary,
            () => props.offsetMainAxis,
            () => props.offsetCrossAxis,
            () => props.placement,
            () => props.strategy,
        ], adjust);
        watch(() => props.target, watchScroll);
        onMounted(adjust);
        onMounted(watchScroll);
        onMounted(() => {
            window.addEventListener('resize', adjust);
            if (window.IntersectionObserver) {
                observer = new IntersectionObserver(adjust);
                observer.observe(root.value);
            }
        });
        onBeforeUnmount(() => {
            window.removeEventListener('resize', adjust);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
        });
        return (_ctx, _cache) => {
            return (openBlock(), createElementBlock("div", mergeProps({
                ref_key: "root",
                ref: root
            }, _ctx.$attrs, { style: unref(style) }), [
                renderSlot(_ctx.$slots, "default")
            ], 16 /* FULL_PROPS */));
        };
    }
});

script$2.__file = "packages/popper/src/UiPopperFloating.vue";

const _hoisted_1 = {
    key: 0,
    class: "omnica-popper__arrow",
    "data-arrow": ""
};
const _hoisted_2 = { class: "omnica-popper__content" };
var script$1 = /*#__PURE__*/ defineComponent({
    __name: 'UiPopper',
    props: {
        /** Функция, возвращающая элемент цели, к которой будет привязан плавающий элемент */
        target: {
            type: Function,
            required: true,
        },
        /** События, по которым производится переключение видимости */
        triggers: {
            type: Array,
            default: () => ['hover'],
        },
        /** События внутри плавающего элемента, по которым производится переключение видимости */
        popperTriggers: {
            type: Array,
            default: () => [],
        },
        /** Флаг для ручного переключения видимости */
        shown: {
            type: Boolean,
            default: false,
        },
        /** Сторона цели, у которой появится плавающий элемент */
        placement: {
            type: String,
            default: 'bottom',
        },
        /** Способ позиционирования, значение CSS свойство position */
        strategy: {
            type: String,
            default: 'absolute',
        },
        /**
         * Контейнер, либо селектор контейнера, внутрь которого будет помещена верстка плавающего элемента.
         * Если не указан, будет взят из глобальных настроек или document.body
         */
        container: {
            type: null,
            validator: (value) => value === null || value instanceof Element || typeof value === 'string',
            default: null,
        },
        /** Элемент, массив элементов, относительно которых будет рассчитываться перекрытие */
        boundary: {
            type: [String, Element, Array],
            default: undefined,
        },
        /** Сдвиг по основной оси, определяемой из положения элемента */
        offsetMainAxis: {
            type: [Number, String],
            validator: (value) => !isNaN(Number(value)),
            default: 8,
        },
        /** Сдвиг по перекрестной оси, определяемой из положения элемента */
        offsetCrossAxis: {
            type: [Number, String],
            validator: (value) => !isNaN(Number(value)),
            default: 0,
        },
        /** Стратегии позиционирования при обнаружении перекрытия */
        overflow: {
            type: Array,
            default: () => ['flip', 'shift'],
        },
        /** Задержка перед показом/скрытием плавающего элемента */
        delay: {
            type: [Number, String, Object],
            validator: (value) => typeof value === 'object' || !isNaN(Number(value)),
            default: 0,
        },
        /** Флаг, отключающий возможность показа плавающего элемента */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Время, через которое плавающий элемент удаляется со страницы, если был скрыт */
        disposeTimeout: {
            type: null,
            default: 5000,
        },
        /** Флаг автоматического сокрытия при событии триггера за пределами элемента */
        autoHide: {
            type: Boolean,
            default: false,
        },
        /** Темная тема */
        dark: {
            type: Boolean,
            default: false,
        },
        /** Добавляет стрелку-указатель */
        withArrow: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        /** Появление плавающего элемента */
        'show',
        /** Сокрытие плавающего элемента */
        'hide',
        /** Сокрытие плавающего элемента по клику вне области контейнера */
        'hide:auto',
        /** Сокрытие плавающего элемента по клику на элемент с директивой vPopperClose */
        'hide:directive',
        /** Смена видимости плавающего элемента */
        'update:shown',
        /** Смена видимости плавающего элемента на отображение на форме */
        'apply:show',
        /** Смена видимости плавающего элемента на сокрытие */
        'apply:hide',
        /** Удаление с формы плавающего элемента */
        'dispose',
    ],
    setup(__props, { expose, emit }) {
        const props = __props;
        const popper = ref(null);
        const popperEvents = new LocalEventHandler();
        const targetEvents = new LocalEventHandler();
        const state = reactive({
            shown: false,
            showing: false,
            hiding: false,
            clicked: false,
            touched: false,
            disposed: false,
        });
        let disposeTimer = undefined;
        let scheduleTimer = undefined;
        const delay = computed(() => normalizeDelay(props.delay));
        const adjust = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (props.target() && !state.disposed) {
                yield ((_a = popper.value) === null || _a === void 0 ? void 0 : _a.adjust());
            }
        });
        const detach = () => { var _a; return (_a = popper.value) === null || _a === void 0 ? void 0 : _a.detach(); };
        const teleport = () => {
            var _a;
            if (props.target() && !state.disposed) {
                (_a = popper.value) === null || _a === void 0 ? void 0 : _a.teleport();
            }
        };
        const schedule = (operation, delay) => {
            clearTimeout(scheduleTimer);
            if (delay > 0) {
                scheduleTimer = setTimeout(() => operation(), delay);
            }
            else {
                operation();
            }
        };
        const show = ({ skipDelay = false, force = false } = {}) => {
            if (force || !props.disabled) {
                state.hiding = false;
                schedule(() => __awaiter(this, void 0, void 0, function* () {
                    clearTimeout(disposeTimer);
                    clearTimeout(scheduleTimer);
                    if (state.shown) {
                        return;
                    }
                    teleport();
                    yield nextAnimationFrame();
                    yield nextAnimationFrame();
                    yield adjust();
                    if (!state.hiding) {
                        state.shown = true;
                        emit('update:shown', true);
                        emit('apply:show');
                    }
                }), skipDelay ? 0 : delay.value.show);
                emit('show');
                state.showing = true;
                requestAnimationFrame(() => setTimeout(() => state.showing = false));
            }
        };
        const hide = (skipDelay = false) => {
            state.hiding = true;
            schedule(() => __awaiter(this, void 0, void 0, function* () {
                clearTimeout(scheduleTimer);
                if (!state.shown) {
                    return;
                }
                state.shown = false;
                emit('update:shown', false);
                clearTimeout(disposeTimer);
                if (props.disposeTimeout !== null) {
                    disposeTimer = setTimeout(() => {
                        if (popper.value) {
                            detach();
                        }
                    }, Number(props.disposeTimeout));
                }
                emit('apply:hide');
            }), skipDelay ? 0 : delay.value.hide);
            emit('hide');
        };
        const onShowTriggered = (event) => {
            if (!state.shown || state.hiding) {
                event.processedByOmnicaPopper = true;
                !state.touched && show();
            }
        };
        const onHideTriggered = (event) => {
            if (!event.processedByOmnicaPopper) {
                hide();
            }
        };
        const enableShowTriggers = (target, events, triggers) => {
            triggers.forEach(t => {
                const eventType = {
                    hover: 'mouseenter',
                    focus: 'focus',
                    click: 'click',
                    touch: 'touchstart',
                }[t];
                if (eventType) {
                    events.on([target], eventType, onShowTriggered);
                }
            });
        };
        const enableHideTriggers = (target, events, triggers) => {
            triggers.forEach(t => {
                const eventType = {
                    hover: 'mouseleave',
                    focus: 'blur',
                    click: 'click',
                    touch: 'touchend',
                }[t];
                if (eventType) {
                    events.on([target], eventType, onHideTriggered);
                }
            });
        };
        const startTargetListening = () => {
            const target = props.target();
            if (target) {
                enableShowTriggers(target, targetEvents, props.triggers);
                enableHideTriggers(target, targetEvents, props.triggers.length > 0 ? [...props.triggers, 'click'] : []);
            }
        };
        const stopTargetListening = () => targetEvents.clear();
        const startPopperListening = () => {
            if (popper.value) {
                enableShowTriggers(popper.value.$el, popperEvents, props.popperTriggers);
                enableHideTriggers(popper.value.$el, popperEvents, props.popperTriggers);
            }
        };
        const stopPopperListening = () => popperEvents.clear();
        const startListening = () => {
            startPopperListening();
            startTargetListening();
        };
        const stopListening = () => {
            stopPopperListening();
            stopTargetListening();
        };
        const renameTargetAttribute = (src, dst) => {
            const target = props.target();
            if (target) {
                renameAttribute(target, src, dst);
            }
        };
        const init = () => {
            if (!state.disposed) {
                return;
            }
            state.disposed = false;
            renameTargetAttribute('title', 'data-original-title');
            detach();
            startListening();
            if (props.shown) {
                show();
            }
        };
        const dispose = () => {
            if (state.disposed) {
                return;
            }
            state.disposed = true;
            stopListening();
            hide(true);
            detach();
            state.shown = false;
            renameTargetAttribute('data-original-title', 'title');
            emit('dispose');
        };
        expose({
            adjust,
            dispose,
            show,
            hide,
        });
        watch(() => props.shown, shown => shown ? show() : hide());
        watch(() => props.target(), () => {
            stopTargetListening();
            startTargetListening();
        });
        watch(() => props.triggers, () => {
            stopTargetListening();
            startTargetListening();
        });
        watch([
            () => state.shown,
            () => props.popperTriggers,
        ], () => {
            stopPopperListening();
            startPopperListening();
        });
        watch(() => props.container, () => __awaiter(this, void 0, void 0, function* () {
            if (state.shown) {
                teleport();
                yield adjust();
            }
        }));
        watch(() => props.disabled, disabled => disabled ? dispose() : init());
        const onGlobalTap = (event, touch = false) => __awaiter(this, void 0, void 0, function* () {
            var _b;
            if (popper.value) {
                const contains = state.clicked || ((_b = popper.value) === null || _b === void 0 ? void 0 : _b.contains(event.target));
                yield nextAnimationFrame();
                if (event.omnicaPopperCloseAll || (event.omnicaPopperClose && contains) || (props.autoHide && !contains)) {
                    if (state.showing) {
                        return;
                    }
                    hide();
                    if (event.omnicaPopperClose) {
                        emit('hide:directive');
                    }
                    else {
                        emit('hide:auto');
                    }
                    if (touch) {
                        state.touched = true;
                        setTimeout(() => state.touched = false, 300);
                    }
                }
            }
        });
        const onGlobalClick = (event) => onGlobalTap(event);
        const onGlobalMousedown = (event) => {
            var _a;
            if (popper.value) {
                state.clicked = (_a = popper.value) === null || _a === void 0 ? void 0 : _a.contains(event.target);
            }
        };
        const onGlobalTouch = (event) => onGlobalTap(event, true);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            state.disposed = true;
            init();
            globalEventHandler.on('click', onGlobalClick);
            globalEventHandler.on('mousedown', onGlobalMousedown);
            globalEventHandler.on('touchend', onGlobalTouch);
        }));
        onActivated(() => props.shown ? show() : hide());
        onDeactivated(() => hide());
        onBeforeUnmount(() => {
            globalEventHandler.off('click', onGlobalClick);
            globalEventHandler.off('mousedown', onGlobalMousedown);
            globalEventHandler.off('touchend', onGlobalTouch);
            dispose();
        });
        return (_ctx, _cache) => {
            return (openBlock(), createBlock(script$2, mergeProps({
                ref_key: "popper",
                ref: popper,
                target: __props.target,
                container: __props.container,
                boundary: __props.boundary,
                "offset-main-axis": __props.offsetMainAxis,
                "offset-cross-axis": __props.offsetCrossAxis,
                placement: __props.placement,
                strategy: __props.strategy,
                overflow: __props.overflow,
                class: {
                    'omnica-popper': true,
                    'omnica-popper_shown': state.shown,
                    'omnica-popper_hidden': !state.shown,
                    'omnica-popper_dark': __props.dark,
                }
            }, _ctx.$attrs), {
                default: withCtx(() => [
                    (__props.withArrow)
                        ? (openBlock(), createElementBlock("div", _hoisted_1))
                        : createCommentVNode("v-if", true),
                    createElementVNode("div", _hoisted_2, [
                        createCommentVNode(" @slot Контент плавающего элемента "),
                        renderSlot(_ctx.$slots, "default")
                    ])
                ]),
                _: 3 /* FORWARDED */
            }, 16 /* FULL_PROPS */, ["target", "container", "boundary", "offset-main-axis", "offset-cross-axis", "placement", "strategy", "overflow", "class"]));
        };
    }
});

script$1.__file = "packages/popper/src/UiPopper.vue";

const arraify = (value) => Array.isArray(value)
    ? [...value]
    : [value];
const render = (content) => arraify(typeof content === 'function'
    ? content(h)
    : content);

var script = defineComponent({
    name: 'OmnicaVNodeContent',
    props: {
        /** Контент для отрисовки */
        content: {
            type: null,
            validator: () => true,
            required: true,
        },
    },
    setup: props => () => render(props.content),
});

script.__file = "packages/scaffolding/src/OmnicaVNodeContent.vue";

let uid = 0;
function createPopper(el, options) {
    const definition = reactive({
        id: ++uid,
        el,
        options,
        shown: false,
    });
    let app = createApp({
        render() {
            const _a = definition.options, { content, disabled } = _a, options = __rest(_a, ["content", "disabled"]);
            return h(script$1, Object.assign(Object.assign({}, options), { key: definition.id, target: () => definition.el, shown: definition.shown || definition.options.shown, disabled: disabled || !content, 'onUpdate:shown': (shown) => el.classList.toggle('omnica-popper-target_active', shown) }), () => render(content !== null && content !== void 0 ? content : ''));
        },
        devtools: {
            hide: true,
        },
    });
    const mountTarget = document.createElement('div');
    document.body.appendChild(mountTarget);
    app.mount(mountTarget);
    if (el.classList) {
        el.classList.add('omnica-popper-target');
    }
    return el.$_omnicaPopper = {
        show() { definition.shown = true; },
        hide() { definition.shown = false; },
        update(options) {
            definition.options = options;
        },
        release() {
            app === null || app === void 0 ? void 0 : app.unmount();
            app = null;
        },
    };
}
function destroyPopper(el) {
    if (el.$_omnicaPopper) {
        el.$_omnicaPopper.release();
        delete el.$_omnicaPopper;
        delete el.$_omnicaPopperOldShown;
    }
    if (el.classList) {
        el.classList.toggle('omnica-popper-target', false);
        el.classList.toggle('omnica-popper-target_active', false);
    }
}
const bind = (el, binding) => {
    var _a, _b, _c, _d, _e;
    const instance = binding.instance;
    // Хак, binding.instance может отсутствовать, но контекст все равно можно извлечь, используя приватные переменные
    // @see https://github.com/vuejs/core/issues/2562
    const parent = el.__vueParentComponent;
    const globals = parent === null || parent === void 0 ? void 0 : parent.appContext.app.config.globalProperties;
    const options = Object.assign({ container: (_d = (_b = (_a = instance === null || instance === void 0 ? void 0 : instance.$omnicaPopper) === null || _a === void 0 ? void 0 : _a.container) !== null && _b !== void 0 ? _b : (_c = globals === null || globals === void 0 ? void 0 : globals.$omnicaPopper) === null || _c === void 0 ? void 0 : _c.container) !== null && _d !== void 0 ? _d : null, content: null, shown: undefined, disabled: false, dark: false }, binding.value);
    if (!options.content || options.disabled) {
        destroyPopper(el);
    }
    else {
        let injection;
        if (el.$_omnicaPopper) {
            injection = el.$_omnicaPopper;
            injection.update(options);
        }
        else {
            injection = createPopper(el, options);
        }
        if (typeof options.shown !== 'undefined' && options.shown !== el.$_omnicaPopperOldShown) {
            el.$_omnicaPopperOldShown = (_e = options.shown) !== null && _e !== void 0 ? _e : false;
            options.shown
                ? injection.show()
                : injection.hide();
        }
    }
};
var vPopper = {
    beforeMount: bind,
    updated: bind,
    beforeUnmount(el) {
        destroyPopper(el);
    },
};

var vPopperClose = {
    beforeMount(el, { value, modifiers }) {
        el.omnicaPopperCloserModifiers = modifiers;
        if (typeof value === 'undefined' || value) {
            addCloserListeners(el);
        }
    },
    updated(el, { value, oldValue, modifiers }) {
        el.omnicaPopperCloserModifiers = modifiers;
        if (value !== oldValue) {
            if (typeof value === 'undefined' || value) {
                addCloserListeners(el);
            }
            else {
                removeCloserListeners(el);
            }
        }
    },
    beforeUnmount(el) {
        removeCloserListeners(el);
    },
};

var index = {
    install(app, options) {
        var _a;
        const globals = app.config.globalProperties;
        const installed = (_a = globals.OmnicaPlugins) !== null && _a !== void 0 ? _a : [];
        if (!installed.includes(this)) {
            app.component('UiPopper', script$1);
            app.directive('popper', vPopper);
            app.directive('popper-close', vPopperClose);
            globals.$omnicaPopper = reactive(Object.assign({ container: null }, options));
            app.provide('$omnicaPopper', globals.$omnicaPopper);
        }
        installed.push(this);
        globals.OmnicaPlugins = installed;
    },
};

export { script$1 as UiPopper, index as default, vPopper, vPopperClose };
